// app-ui/src/pages/ProjectDashboard.tsx
import React, { useState, useRef, useCallback, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import CreateProjectForm from '../components/CreateProjectForm';
import ProjectList from '../components/ProjectList';
import CanvasViewer from '../../tabs/requirements/components/canvas/CanvasViewer';
import { CanvasOutlinePanel } from '../../tabs/requirements/components/canvas/CanvasOutlinePanel';
import {
    RequirementsCanvasOutlineProvider,
    useRequirementsCanvasOutline,
} from '../../tabs/requirements/components/canvas/requirementsCanvasOutlineContext';
import ArchitectureTab from '../../tabs/architecture/components/ArchitectureTab';
import CodeTab from '../../tabs/code/components/CodeTab';
import DocumentViewer from '../../tabs/documents/components/DocumentViewer';
import { Preview } from '../../tabs/preview';
import Settings from '../../profile/Settings';
import GitSettings from '../components/settings/GitSettings';
import ShareProjectModal from '../components/ShareProjectModal';
import { VishCoderChatPanel } from '../../channels/chat';
import { DiscoveryProceedPauseProvider } from '../../channels/discovery/DiscoveryProceedPauseContext';
import { listNotifications, markNotificationRead, type Notification } from '../../notifications/api/notificationsApi';
import ErrorBoundary from '../../../../shared/components/ErrorBoundary';
import BuildChannelHandler from '../../channels/build_feature/components/BuildChannelHandler';
import { BottomPanelInsetProvider } from '../../channels/build_feature/components/bottom-panel-inset-context';
import { useAuth } from '../../../../features/system/auth/hooks/useAuth';
import apiClient, { TokenManager } from '../../../../shared/lib/apiClient';
import type { ProjectRequirementsResponse } from '../../../../shared/types/project';
import ProductDiscoveryChat from '../../channels/discovery/ProductDiscoveryChat';
import DiscoveryDocumentsFromChat from '../../tabs/requirements/components/documents/DiscoveryDocumentsFromChat';
import LlmService from '../../channels/chat/api/llmApi';
import { initializeWebSocketConnection } from '../../../../shared/lib/comm/gateway_ws/connection';
import { GitHubService } from '../../tabs/code';
import {
    getProjectGitSettingsFromStorage,
    saveProjectGitSettingsToStorage,
    GITHUB_SETTINGS_STORAGE_KEY,
} from '../../../../shared/lib/storageUtils';
import { hasAnyInProgressBuild, getInProgressFeatures, getLatestBuild } from '../../../../shared/lib/buildStatusTracker';
import { getProject, updateProjectGitSettings } from '../api/projectsApi';
import { hydrateProjectGitSettingsFromServerIfEmpty } from '../lib/projectGitSettingsHydration';
import type { Project } from '../types';

/** Renders resizable outline under the project list when the requirements canvas is active. */
const RequirementsSidebarOutlineSlot: React.FC<{
    activeTab: 'requirements' | 'git' | 'sandbox';
    requirementsPane: 'documents' | 'canvas';
    selectedProjectId: number | string | null;
    outlineHeightPx: number;
    onOutlineHeightChange: (px: number) => void;
    onOutlineResizeEnd: (px: number) => void;
}> = ({
    activeTab,
    requirementsPane,
    selectedProjectId,
    outlineHeightPx,
    onOutlineHeightChange,
    onOutlineResizeEnd,
}) => {
    const { outlinePanel } = useRequirementsCanvasOutline();
    const dragRef = useRef<{ startY: number; startH: number } | null>(null);
    const lastHeightRef = useRef(outlineHeightPx);

    useEffect(() => {
        lastHeightRef.current = outlineHeightPx;
    }, [outlineHeightPx]);

    const visible =
        activeTab === 'requirements' &&
        requirementsPane === 'canvas' &&
        selectedProjectId != null &&
        !!outlinePanel;

    const onResizeMouseDown = useCallback(
        (e: React.MouseEvent) => {
            e.preventDefault();
            dragRef.current = { startY: e.clientY, startH: outlineHeightPx };
            const onMove = (ev: MouseEvent) => {
                const d = dragRef.current;
                if (!d) return;
                const dy = ev.clientY - d.startY;
                const next = Math.min(560, Math.max(100, Math.round(d.startH - dy)));
                lastHeightRef.current = next;
                onOutlineHeightChange(next);
            };
            const onUp = () => {
                dragRef.current = null;
                document.removeEventListener('mousemove', onMove);
                document.removeEventListener('mouseup', onUp);
                document.body.style.removeProperty('cursor');
                document.body.style.removeProperty('user-select');
                onOutlineResizeEnd(lastHeightRef.current);
            };
            document.addEventListener('mousemove', onMove);
            document.addEventListener('mouseup', onUp);
            document.body.style.cursor = 'row-resize';
            document.body.style.userSelect = 'none';
        },
        [outlineHeightPx, onOutlineHeightChange, onOutlineResizeEnd]
    );

    if (!visible || !outlinePanel) {
        return null;
    }

    const h = Math.min(560, Math.max(100, outlineHeightPx));

    return (
        <>
            <div
                role="separator"
                aria-orientation="horizontal"
                className="group flex h-3 shrink-0 cursor-row-resize items-center justify-center border-t border-white/10 bg-white/[0.03] hover:bg-white/10"
                onMouseDown={onResizeMouseDown}
                title="Drag to resize outline"
            >
                <div className="h-1 w-10 rounded-full bg-white/25 group-hover:bg-white/40" />
            </div>
            <div
                className="flex min-h-0 shrink-0 flex-col border-t border-white/10"
                style={{ height: h }}
            >
                <CanvasOutlinePanel {...outlinePanel} layout="sidebar" />
            </div>
        </>
    );
};

const ProjectDashboard: React.FC = () => {
    // Auth context
    const { signOut, isAuthenticated, user } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    
    // Existing state
    const [selectedProjectId, setSelectedProjectId] = useState<number | string | null>(null);
    const [selectedProjectName, setSelectedProjectName] = useState<string>('');
    const [selectedProjectPermission, setSelectedProjectPermission] = useState<'owner' | 'read' | 'readwrite' | null>(null);
    const [listRefreshKey, setListRefreshKey] = useState<number>(0);
    const [canvasRefreshNonce, setCanvasRefreshNonce] = useState<number>(0);

    // Mobile responsive state - Start with sidebar open on desktop for easier project selection
    const [isSidebarOpen, setIsSidebarOpen] = useState(true);
    const [isMobile, setIsMobile] = useState(false);

    const SIDEBAR_OUTLINE_H_KEY = 'vishmaker_sidebar_outline_height_px';
    const [sidebarOutlineHeightPx, setSidebarOutlineHeightPx] = useState(() => {
        if (typeof window === 'undefined') return 240;
        try {
            const raw = localStorage.getItem(SIDEBAR_OUTLINE_H_KEY);
            if (raw) {
                const n = parseInt(raw, 10);
                if (!Number.isNaN(n)) return Math.min(560, Math.max(100, n));
            }
        } catch {
            /* ignore */
        }
        return 240;
    });

    const handleSidebarOutlineHeightChange = useCallback((px: number) => {
        setSidebarOutlineHeightPx(Math.min(560, Math.max(100, px)));
    }, []);

    const handleSidebarOutlineResizeEnd = useCallback((px: number) => {
        const clamped = Math.min(560, Math.max(100, px));
        try {
            localStorage.setItem(SIDEBAR_OUTLINE_H_KEY, String(clamped));
        } catch {
            /* ignore */
        }
    }, []);
    
    // Settings state
    const [isSettingsOpen, setIsSettingsOpen] = useState(false);
    const [settingsInitialTab, setSettingsInitialTab] = useState<'tech-stack' | 'settings' | 'llm-config'>('tech-stack');
    const [isGitSettingsOpen, setIsGitSettingsOpen] = useState(false);
    /**
     * Which project Git Settings edits: `undefined` = follow sidebar selection;
     * explicit id = that project (e.g. from discovery); explicit `null` = browser defaults when no project exists yet.
     */
    const [gitSettingsScopeProjectId, setGitSettingsScopeProjectId] = useState<number | string | null | undefined>(
        undefined
    );

    const gitSettingsEffectiveProjectId =
        gitSettingsScopeProjectId === undefined ? selectedProjectId : gitSettingsScopeProjectId;

    // Tab state - new structure
    const [activeTab, setActiveTab] = useState<'requirements' | 'git' | 'sandbox'>('requirements');
    const [activeSubmenu, setActiveSubmenu] = useState<'architecture' | 'code' | 'documents' | 'preview' | null>(null);
    const [selectedBranch, setSelectedBranch] = useState<string>('main');
    const [availableBranches, setAvailableBranches] = useState<string[]>([]);
    const [sandboxBranch, setSandboxBranch] = useState<string>('');
    const [sandboxCommit, setSandboxCommit] = useState<string>('');
    const [hasInProgressBuild, setHasInProgressBuild] = useState<boolean>(false);
    
    // Panel visibility states
    const [isBottomPanelOpen, setIsBottomPanelOpen] = useState(false);
    const [isRightPanelOpen, setIsRightPanelOpen] = useState(false);
    const [isCreateProjectChatMode, setIsCreateProjectChatMode] = useState(false);
    const [isShareModalOpen, setIsShareModalOpen] = useState(false);
    // Notifications (mention tags)
    const [notifications, setNotifications] = useState<Notification[]>([]);
    const [notificationsUnreadCount, setNotificationsUnreadCount] = useState(0);
    const [notificationsDropdownOpen, setNotificationsDropdownOpen] = useState(false);
    const [notificationsLoading, setNotificationsLoading] = useState(false);
    const notificationsDropdownRef = useRef<HTMLDivElement>(null);
    // Deep link: open a specific requirement/node and optionally scroll to comment (set when user clicks a notification)
    const [deepLinkOpen, setDeepLinkOpen] = useState<{ targetType: string; targetUiid: string; commentId?: string } | null>(null);
    // Preview selection state (from Preview component)
    const [previewSelection, setPreviewSelection] = useState<Record<string, any> | null>(null);

    // Refresh triggers for tabs
    const [architectureRefreshTrigger, setArchitectureRefreshTrigger] = useState(0);
    const [codeRefreshTrigger, setCodeRefreshTrigger] = useState(0);
    const [documentsRefreshTrigger, setDocumentsRefreshTrigger] = useState(0);

    /** null = loading gate (purple); false = discovery (no flows); true = canvas */
    const [projectHasFlows, setProjectHasFlows] = useState<boolean | null>(null);
    /** Populated when the gate fetch finds flows so CanvasViewer can hydrate without a second GET. */
    const [requirementsSeed, setRequirementsSeed] = useState<ProjectRequirementsResponse | null>(null);
    /** Requirements sub-tab; cleared when discovery vs canvas route (`projectHasFlows`) changes so defaults apply. */
    const [requirementsPaneOverride, setRequirementsPaneOverride] = useState<'documents' | 'canvas' | null>(null);
    const [selectedProjectInitialPrompt, setSelectedProjectInitialPrompt] = useState<string>('');
    const [discoveryBuildBusy, setDiscoveryBuildBusy] = useState(false);
    const [discoveryProceeded, setDiscoveryProceeded] = useState(false);

    useEffect(() => {
        setRequirementsPaneOverride(null);
    }, [projectHasFlows]);

    const requirementsPane: 'documents' | 'canvas' =
        requirementsPaneOverride ??
        (projectHasFlows === false ? 'documents' : projectHasFlows === true ? 'canvas' : 'documents');

    // Track build status from buildStatusTracker
    useEffect(() => {
        if (!selectedProjectId) {
            setHasInProgressBuild(false);
            setSandboxBranch('');
            setSandboxCommit('');
            // Switch away from sandbox tab if no project is selected
            if (activeTab === 'sandbox') {
                setActiveTab('requirements');
            }
            return;
        }
        
        const updateBuildStatus = () => {
            const hasInProgress = hasAnyInProgressBuild(selectedProjectId);
            setHasInProgressBuild(hasInProgress);
            
            // If there are in-progress builds, try to get branch and commit info
            if (hasInProgress) {
                const inProgressFeatures = getInProgressFeatures(selectedProjectId);
                if (inProgressFeatures.length > 0) {
                    // Get latest build for the first in-progress feature
                    const latestBuild = getLatestBuild(selectedProjectId, inProgressFeatures[0]);
                    if (latestBuild && latestBuild.git_commit) {
                        setSandboxCommit(latestBuild.git_commit);
                    }
                }
                
                // Try to get branch from project-level GitHub settings
                try {
                    const githubSettings = getProjectGitSettingsFromStorage(selectedProjectId);
                    if (githubSettings?.branch) {
                        setSandboxBranch(githubSettings.branch);
                    }
                } catch (error) {
                    console.error('Failed to get branch from GitHub settings:', error);
                }
            } else {
                // Clear branch and commit when no builds are in progress
                setSandboxBranch('');
                setSandboxCommit('');
                // Switch away from sandbox tab if no builds are in progress (unless discovery proceeded)
                if (activeTab === 'sandbox' && !discoveryProceeded) {
                    setActiveTab('requirements');
                }
            }
        };
        
        // Check initial state
        updateBuildStatus();
        
        // Poll for changes in build status (check every 1 second)
        const intervalId = setInterval(updateBuildStatus, 1000);
        
        // Also listen for storage events (in case build status is updated in another tab/window)
        const handleStorageChange = (e: StorageEvent) => {
            if (e.key === 'trackBuildStatus' || e.key === GITHUB_SETTINGS_STORAGE_KEY) {
                updateBuildStatus();
            }
        };
        window.addEventListener('storage', handleStorageChange);
        
        // Listen for custom build status change events
        const handleBuildStatusChange = () => {
            updateBuildStatus();
        };
        window.addEventListener('buildStatusChanged', handleBuildStatusChange);
        
        return () => {
            clearInterval(intervalId);
            window.removeEventListener('storage', handleStorageChange);
            window.removeEventListener('buildStatusChanged', handleBuildStatusChange);
        };
    }, [selectedProjectId, activeTab, discoveryProceeded]);

    // Initialize WebSocket connection and health check when a project is opened
    useEffect(() => {
        if (selectedProjectId !== null) {
            console.log(`🔌 [ProjectHomePage] Project ${selectedProjectId} opened - initializing WebSocket connection via outbound.ts...`);
            // Initialize WebSocket connection through outbound.ts
            // This will automatically trigger health check when connection is established
            initializeWebSocketConnection();
        }
    }, [selectedProjectId]);

    useEffect(() => {
        if (selectedProjectId === null || selectedProjectId === undefined || String(selectedProjectId).trim() === '') {
            return;
        }
        void hydrateProjectGitSettingsFromServerIfEmpty(selectedProjectId);
    }, [selectedProjectId]);

    // Set default submenu when switching to Git or Sandbox tabs
    useEffect(() => {
        if (activeTab === 'git' && !activeSubmenu) {
            setActiveSubmenu('architecture');
        } else if (activeTab === 'sandbox' && !activeSubmenu) {
            setActiveSubmenu('preview');
        }
    }, [activeTab, activeSubmenu]);

    // Load available branches when Git tab is active (uses project-level git settings)
    useEffect(() => {
        if (activeTab === 'git') {
            const loadBranches = async () => {
                try {
                    const settings = getProjectGitSettingsFromStorage(selectedProjectId);
                    if (settings?.repo_url) {
                        const repoInfo = GitHubService.parseRepoPath(settings.repo_url);
                        if (repoInfo) {
                            try {
                                const branches = await GitHubService.getBranches(
                                    repoInfo.owner,
                                    repoInfo.repo,
                                    settings.token
                                );
                                setAvailableBranches(branches.map(b => b.name));
                                if (settings.branch) {
                                    setSelectedBranch(settings.branch);
                                }
                            } catch (error) {
                                console.error('Failed to load branches:', error);
                            }
                        }
                    }
                } catch (error) {
                    console.error('Failed to load GitHub settings:', error);
                }
            };
            loadBranches();
        }
    }, [activeTab, selectedProjectId]);

    // Handle refresh based on active tab and submenu
    const handleRefresh = () => {
        if (activeTab === 'requirements') {
            setCanvasRefreshNonce(Date.now());
        } else if (activeTab === 'git' || activeTab === 'sandbox') {
            if (activeSubmenu === 'architecture') {
                setArchitectureRefreshTrigger(prev => prev + 1);
            } else if (activeSubmenu === 'code') {
                setCodeRefreshTrigger(prev => prev + 1);
            } else if (activeSubmenu === 'documents') {
                setDocumentsRefreshTrigger(prev => prev + 1);
            }
        }
    };

    // Check if mobile on mount and resize
    useEffect(() => {
        const checkMobile = () => {
            const mobile = window.innerWidth < 1024; // lg breakpoint
            setIsMobile(mobile);
            
            // Auto-close sidebar on mobile when not explicitly opened
            if (mobile && !isSidebarOpen) {
                setIsSidebarOpen(false);
            }
            // Keep sidebar open by default on desktop for easier project selection
            if (!mobile && isSidebarOpen === undefined) {
                setIsSidebarOpen(true);
            }
        };

        // Initial check
        checkMobile();

        // Set up resize listener
        window.addEventListener('resize', checkMobile);
        return () => window.removeEventListener('resize', checkMobile);
    }, [isSidebarOpen]);

    // Prevent body scroll when mobile sidebar is open
    useEffect(() => {
        if (isMobile && isSidebarOpen) {
            document.body.style.overflow = 'hidden';
        } else {
            document.body.style.overflow = 'unset';
        }

        return () => {
            document.body.style.overflow = 'unset';
        };
    }, [isMobile, isSidebarOpen]);

    // Use refs to avoid issues with re-rendering
    const selectedProjectIdRef = useRef<number | string | null>(selectedProjectId);
    selectedProjectIdRef.current = selectedProjectId;

    const triggerListRefresh = useCallback(() => {
        setListRefreshKey(prev => prev + 1);
    }, []);

    const handleProjectCreated = useCallback(
        (
            projectId?: number | string,
            projectName?: string,
            options?: { openDiscoveryChat?: boolean }
        ) => {
            triggerListRefresh();

            if (projectId) {
                setSelectedProjectId(projectId);
                setSelectedProjectName(projectName || '');
                setCanvasRefreshNonce(Date.now());
                if (options?.openDiscoveryChat) {
                    setIsRightPanelOpen(true);
                }
            }

            if (isMobile) {
                setIsSidebarOpen(false);
            }
        },
        [triggerListRefresh, isMobile]
    );

    const handleProjectSelected = useCallback((projectId: number | string, projectName?: string, permission?: 'owner' | 'read' | 'readwrite') => {
        setSelectedProjectId(projectId);
        setSelectedProjectName(projectName || '');
        setSelectedProjectPermission(permission ?? null);
        setProjectHasFlows(null);
        setDiscoveryProceeded(false);
        // Reset to Requirements tab
        setActiveTab('requirements');
        setActiveSubmenu(null);
        // Auto-refresh all tabs when a project is selected
        setCanvasRefreshNonce(Date.now());
        setArchitectureRefreshTrigger(prev => prev + 1);
        setCodeRefreshTrigger(prev => prev + 1);
        setDocumentsRefreshTrigger(prev => prev + 1);
        // Close mobile sidebar after selecting project
        if (isMobile) {
            setIsSidebarOpen(false);
        }
    }, [isMobile]);

    const handleGitSettingsFromProjectList = useCallback(
        (project: Project) => {
            if (selectedProjectId !== project.id) {
                handleProjectSelected(project.id, project.name, project.permission);
            }
            setGitSettingsScopeProjectId(undefined);
            setIsGitSettingsOpen(true);
        },
        [selectedProjectId, handleProjectSelected]
    );

    useEffect(() => {
        if (selectedProjectId == null) {
            setProjectHasFlows(null);
            setRequirementsSeed(null);
            return;
        }
        let cancelled = false;
        setProjectHasFlows(null);
        setRequirementsSeed(null);
        (async () => {
            try {
                const res = await apiClient<ProjectRequirementsResponse>(`/requirements/${selectedProjectId}`);
                if (cancelled) return;
                const hasFlows = (res.flows?.length ?? 0) > 0;
                setProjectHasFlows(hasFlows);
                setRequirementsSeed(hasFlows ? res : null);
            } catch {
                if (!cancelled) {
                    setProjectHasFlows(false);
                    setRequirementsSeed(null);
                }
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [selectedProjectId]);

    useEffect(() => {
        if (projectHasFlows === true) setDiscoveryProceeded(false);
    }, [projectHasFlows]);

    useEffect(() => {
        if (selectedProjectId == null) {
            setSelectedProjectInitialPrompt('');
            return;
        }
        let cancelled = false;
        getProject(selectedProjectId)
            .then((p) => {
                if (!cancelled) setSelectedProjectInitialPrompt(p.initial_prompt ?? '');
            })
            .catch(() => {
                if (!cancelled) setSelectedProjectInitialPrompt('');
            });
        return () => {
            cancelled = true;
        };
    }, [selectedProjectId]);

    const lastDiscoveryAutoOpenKey = useRef<string | null>(null);
    useEffect(() => {
        if (selectedProjectId == null || projectHasFlows !== false || activeTab !== 'requirements') {
            return;
        }
        const key = String(selectedProjectId);
        if (lastDiscoveryAutoOpenKey.current === key) return;
        lastDiscoveryAutoOpenKey.current = key;
        setIsRightPanelOpen(true);
    }, [selectedProjectId, projectHasFlows, activeTab]);

    const handleDiscoveryBuildFromProject = useCallback(
        async (existingProjectId?: number) => {
            const pidRaw = existingProjectId ?? selectedProjectId;
            if (pidRaw == null) return;
            const pid = String(pidRaw).trim();
            if (!pid) return;
            const text = selectedProjectInitialPrompt.trim();
            if (!text) return;
            const userId = TokenManager.getUserId();
            if (!userId) return;
            setDiscoveryBuildBusy(true);
            try {
                await LlmService.processWithLlm('gen_userflow', text, pid, true, () => {});
                setCanvasRefreshNonce(Date.now());
                setProjectHasFlows(true);
            } catch (e) {
                console.error('Discovery build failed:', e);
            } finally {
                setDiscoveryBuildBusy(false);
            }
        },
        [selectedProjectId, selectedProjectInitialPrompt]
    );

    /** Instant Draft in discovery chat — same `gen_userflow` path as legacy “Build with agents”, using live prompt text. */
    const handleDiscoveryUserFlowDraft = useCallback(async (promptText: string, projectId: string | number) => {
        const pid = String(projectId).trim();
        if (!pid || !promptText.trim()) return;
        if (!TokenManager.getUserId()) {
            throw new Error('User ID not found. Please sign in again.');
        }
        setDiscoveryBuildBusy(true);
        try {
            await LlmService.processWithLlm('gen_userflow', promptText.trim(), pid, true, () => {});
            setCanvasRefreshNonce(Date.now());
            setProjectHasFlows(true);
        } catch (e) {
            console.error('Instant draft (user flow) failed:', e);
            throw e instanceof Error ? e : new Error(String(e));
        } finally {
            setDiscoveryBuildBusy(false);
        }
    }, []);

    const handleNewProjectClick = useCallback(() => {
        // Stay on dashboard and reset to the new-project empty state
        setSelectedProjectId(null);
        setSelectedProjectName('');
        setSelectedProjectPermission(null);
        setIsCreateProjectChatMode(false);
        setActiveTab('requirements');
        setActiveSubmenu(null);
        setDeepLinkOpen(null);
        navigate('/dashboard');
    }, [navigate]);

    const canEditProject = selectedProjectPermission === 'owner' || selectedProjectPermission === 'readwrite';
    const isProjectOwner = selectedProjectPermission === 'owner';

    // Fetch notifications when dropdown opens or on mount (for badge)
    const fetchNotifications = useCallback(async () => {
        setNotificationsLoading(true);
        try {
            const res = await listNotifications(50);
            setNotifications(res.notifications || []);
            setNotificationsUnreadCount(res.unread_count ?? 0);
        } catch {
            setNotifications([]);
            setNotificationsUnreadCount(0);
        } finally {
            setNotificationsLoading(false);
        }
    }, []);
    useEffect(() => {
        if (notificationsDropdownOpen) {
            fetchNotifications();
        }
    }, [notificationsDropdownOpen, fetchNotifications]);

    useEffect(() => {
        if (!notificationsDropdownOpen) return;
        const onPointerDown = (e: PointerEvent) => {
            const root = notificationsDropdownRef.current;
            if (root && !root.contains(e.target as Node)) {
                setNotificationsDropdownOpen(false);
            }
        };
        document.addEventListener('pointerdown', onPointerDown, true);
        return () => document.removeEventListener('pointerdown', onPointerDown, true);
    }, [notificationsDropdownOpen]);
    // Initial load for badge (once when authenticated)
    useEffect(() => {
        if (isAuthenticated) {
            fetchNotifications();
        }
    }, [isAuthenticated, fetchNotifications]);

    const handleNotificationClick = useCallback(
        async (n: Notification) => {
            if (!n.project_id || !n.target_type || !n.target_uiid) return;
            try {
                if (!n.read) {
                    await markNotificationRead(n.notification_id);
                    setNotificationsUnreadCount((c) => Math.max(0, c - 1));
                }
            } catch {
                // ignore
            }
            setNotificationsDropdownOpen(false);
            setSelectedProjectId(n.project_id);
            setSelectedProjectName(n.project_name || '');
            setActiveTab('requirements');
            setActiveSubmenu(null);
            setDeepLinkOpen({
                targetType: n.target_type,
                targetUiid: n.target_uiid,
                commentId: n.comment_id
            });
            setCanvasRefreshNonce(Date.now());
        },
        []
    );

    // Handle branch change (persist to project-level git settings: localStorage + API)
    const handleBranchChange = async (branch: string) => {
        setSelectedBranch(branch);
        try {
            const currentSettings = getProjectGitSettingsFromStorage(selectedProjectId, {
                includeGlobalFallback: false,
            });
            if (currentSettings) {
                const updated = { ...currentSettings, branch };
                saveProjectGitSettingsToStorage(selectedProjectId, updated);
                if (selectedProjectId != null) {
                    try {
                        await updateProjectGitSettings(selectedProjectId, updated);
                    } catch (e) {
                        console.debug('Failed to persist branch change to API:', e);
                    }
                }
            }
        } catch (error) {
            console.error('Failed to update branch in settings:', error);
        }
        // Refresh current submenu if active
        if (activeSubmenu) {
            if (activeSubmenu === 'architecture') {
                setArchitectureRefreshTrigger(prev => prev + 1);
            } else if (activeSubmenu === 'code') {
                setCodeRefreshTrigger(prev => prev + 1);
            } else if (activeSubmenu === 'documents') {
                setDocumentsRefreshTrigger(prev => prev + 1);
            }
        }
    };

    const toggleSidebar = () => {
        console.log('ProjectDashboard: toggleSidebar called, current state:', isSidebarOpen);
        setIsSidebarOpen(!isSidebarOpen);
    };

    const closeSidebar = () => {
        setIsSidebarOpen(false);
    };

    // Panel toggle functions
    const toggleBottomPanel = () => {
        console.log('ProjectDashboard: toggleBottomPanel called, current state:', isBottomPanelOpen);
        setIsBottomPanelOpen(prev => {
            const newState = !prev;
            console.log('ProjectDashboard: Bottom panel state changing from', prev, 'to', newState);
            return newState;
        });
    };

    const toggleRightPanel = () => {
        if (selectedProjectId === null) {
            setIsCreateProjectChatMode(prev => !prev);
            return;
        }
        console.log('ProjectDashboard: toggleRightPanel called, current state:', isRightPanelOpen);
        setIsRightPanelOpen(prev => {
            const newState = !prev;
            console.log('ProjectDashboard: Right panel state changing from', prev, 'to', newState);
            return newState;
        });
    };

    useEffect(() => {
        if (selectedProjectId !== null) {
            setIsCreateProjectChatMode(false);
        }
    }, [selectedProjectId]);

    return (
        <RequirementsCanvasOutlineProvider>
        <div className="flex h-screen bg-[#0A071B] text-white overflow-hidden">
            {/* Animated Background */}
            <div className="fixed inset-0 z-0">
                <div className="absolute inset-0 bg-gradient-to-br from-[#0A071B] via-[#1A103A] to-[#0A071B]"></div>
                <div className="absolute -top-1/4 -left-1/4 w-1/2 h-1/2 bg-gradient-to-br from-purple-900/20 to-transparent rounded-full blur-3xl animate-pulse" style={{ animationDuration: '20s' }}></div>
                <div className="absolute -bottom-1/4 -right-1/4 w-1/2 h-1/2 bg-gradient-to-tl from-teal-800/15 to-transparent rounded-full blur-3xl animate-pulse" style={{ animationDuration: '20s', animationDelay: '10s' }}></div>
            </div>

            {/* Mobile Header */}
            <div className="lg:hidden fixed top-0 left-0 right-0 z-30 bg-white/5 backdrop-blur-lg border-b border-white/10 px-4 py-3">
                <div className="flex items-center justify-between">
                    <h1 className="text-xl font-bold bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">
                        VishMaker
                    </h1>
                    <button
                        onClick={toggleSidebar}
                        className="touch-target text-gray-300 hover:text-white transition-colors p-2 rounded-lg hover:bg-white/10"
                        aria-label="Toggle sidebar"
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h4v12H4zM11 7h9M11 12h9M11 17h9" />
                        </svg>
                    </button>
                </div>
                
                {/* Mobile project info bar */}
                {selectedProjectId !== null && (
                    <div className="mt-2 text-sm text-gray-300 bg-white/5 rounded-lg px-3 py-1">
                        Project {selectedProjectId} selected
                    </div>
                )}
            </div>

            {/* Sidebar Overlay (Mobile) */}
            {isMobile && isSidebarOpen && (
                <div 
                    className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40"
                    onClick={closeSidebar}
                />
            )}

            {/* Sidebar — z-20 on desktop so React Flow panels (minimap, controls) in main (z-10) cannot stack above project list actions */}
            <div className={`
                ${isMobile 
                    ? `fixed top-0 left-0 h-full w-80 transform transition-transform duration-300 ease-out z-50 ${isSidebarOpen ? 'translate-x-0' : '-translate-x-full'}`
                    : `relative z-40 transition-all duration-300 ease-out ${isSidebarOpen ? 'w-80 xl:w-96' : 'w-0'} overflow-hidden`
                }
                flex min-h-0 flex-col bg-white/5 backdrop-blur-xl border-r border-white/10
            `}>
                {/* Sidebar Header */}
                <div className="flex items-center justify-between p-6 border-b border-white/10 bg-white/5">
                    <h1 className="text-lg sm:text-xl font-bold bg-gradient-to-r from-purple-400 to-blue-400 bg-clip-text text-transparent">
                        VishMaker
                    </h1>
                    <button
                        onClick={toggleSidebar}
                        className="p-2 rounded-lg transition-colors text-gray-300 hover:bg-white/20 hover:text-white"
                        aria-label={isSidebarOpen ? "Close sidebar" : "Open sidebar"}
                        title={isSidebarOpen ? "Close sidebar" : "Open sidebar"}
                    >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h4v12H4zM11 7h9M11 12h9M11 17h9" />
                        </svg>
                    </button>
                </div>

                {/* Sidebar: projects (scroll) + outline (requirements canvas) */}
                <div className="flex min-h-0 flex-1 flex-col">
                    <div className="min-h-0 flex-1 overflow-y-auto scrollbar-hide">
                        <div className="space-y-6 p-6">
                            <button
                                onClick={handleNewProjectClick}
                                className="w-full inline-flex items-center justify-center gap-2 rounded-xl border border-fuchsia-400/35 bg-gradient-to-r from-fuchsia-500/20 to-violet-500/20 px-4 py-3 text-fuchsia-100 transition-all duration-200 hover:from-fuchsia-500/30 hover:to-violet-500/30 hover:text-white"
                                title="Create a new project"
                            >
                                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                                </svg>
                                New Project
                            </button>
                            <div className="flex-1">
                                <ProjectList
                                    onProjectSelect={handleProjectSelected}
                                    refreshTrigger={listRefreshKey}
                                    selectedProjectId={selectedProjectId}
                                    triggerRefresh={triggerListRefresh}
                                    onGitSettingsClick={handleGitSettingsFromProjectList}
                                />
                            </div>
                        </div>
                    </div>
                    <RequirementsSidebarOutlineSlot
                        activeTab={activeTab}
                        requirementsPane={requirementsPane}
                        selectedProjectId={selectedProjectId}
                        outlineHeightPx={sidebarOutlineHeightPx}
                        onOutlineHeightChange={handleSidebarOutlineHeightChange}
                        onOutlineResizeEnd={handleSidebarOutlineResizeEnd}
                    />
                </div>

            </div>

            {/* Main Content Area - Expands when sidebar is closed (provider: chat panel clears bottom terminal height) */}
            <BottomPanelInsetProvider>
            <div className={`
                flex-1 flex flex-col overflow-hidden relative z-10
                ${isMobile ? 'pt-16' : ''}
                transition-all duration-300 ease-out
            `}>
                {/* Desktop Header — z-30 so notification popover stacks above tab/submenu row (later siblings + backdrop-blur) */}
                <div className="hidden lg:flex relative z-30 items-center justify-between p-6 bg-white/5 backdrop-blur-lg border-b border-white/10">
                    <div className="flex items-center space-x-3">
                        {!isSidebarOpen && (
                            <button
                                onClick={toggleSidebar}
                                className="p-2 rounded-lg transition-colors text-gray-300 hover:bg-white/20 hover:text-white bg-white/10"
                                aria-label="Open sidebar"
                                title="Open sidebar"
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h4v12H4zM11 7h9M11 12h9M11 17h9" />
                                </svg>
                            </button>
                        )}
                        <h2 className="text-xl font-semibold text-white">
                            {selectedProjectId !== null ? (selectedProjectName || `Project ${selectedProjectId}`) : 'No project selected'}
                        </h2>
                    </div>
                    
                    {/* Desktop actions */}
                    <div className="flex items-center space-x-3">
                        {/* Panel Controls */}
                        <div className="flex items-center space-x-2">
                            {/* Bottom Panel Toggle */}
                            <button
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    console.log('ProjectDashboard: Bottom panel button clicked');
                                    toggleBottomPanel();
                                }}
                                className={`p-2 rounded-lg transition-colors text-gray-300 hover:bg-white/20 ${
                                    isBottomPanelOpen ? 'bg-green-500/20 border border-green-500/30' : 'bg-white/10'
                                }`}
                                title={`Toggle Terminal ${isBottomPanelOpen ? '(Open)' : '(Closed)'}`}
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                </svg>
                            </button>
                            
                            {/* Right Panel Toggle */}
                            <button
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    console.log('ProjectDashboard: Right panel button clicked');
                                    toggleRightPanel();
                                }}
                                className={`p-2 rounded-lg transition-colors text-gray-300 hover:bg-white/20 ${
                                    (selectedProjectId === null ? isCreateProjectChatMode : isRightPanelOpen)
                                        ? 'bg-blue-500/20 border border-blue-500/30'
                                        : 'bg-white/10'
                                }`}
                                title={`Toggle Chat Panel ${(selectedProjectId === null ? isCreateProjectChatMode : isRightPanelOpen) ? '(Open)' : '(Closed)'}`}
                            >
                                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.0M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                                </svg>
                            </button>

                            {/* Notifications (mentions) */}
                            <div className="relative" ref={notificationsDropdownRef}>
                                <button
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setNotificationsDropdownOpen((open) => !open);
                                    }}
                                    className="p-2 rounded-lg transition-colors text-gray-300 hover:bg-white/20 bg-white/10 relative"
                                    title="Notifications"
                                >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                                    </svg>
                                    {notificationsUnreadCount > 0 && (
                                        <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 flex items-center justify-center bg-red-500 text-white text-xs font-bold rounded-full">
                                            {notificationsUnreadCount > 99 ? '99+' : notificationsUnreadCount}
                                        </span>
                                    )}
                                </button>
                                {notificationsDropdownOpen && (
                                        <div className="absolute right-0 mt-2 w-96 max-h-80 overflow-y-auto bg-gray-800 border border-white/20 rounded-lg shadow-xl z-50 py-2">
                                            <div className="px-3 py-2 border-b border-white/10 text-sm font-medium text-gray-300">
                                                Notifications
                                            </div>
                                            {notificationsLoading ? (
                                                <div className="px-3 py-4 text-sm text-gray-400">Loading…</div>
                                            ) : notifications.length === 0 ? (
                                                <div className="px-3 py-4 text-sm text-gray-400">No notifications</div>
                                            ) : (
                                                <ul className="divide-y divide-white/10">
                                                    {notifications.map((n) => (
                                                        <li key={n.notification_id}>
                                                            <button
                                                                type="button"
                                                                onClick={() => handleNotificationClick(n)}
                                                                className={`w-full text-left px-3 py-2 text-sm hover:bg-white/10 transition-colors ${!n.read ? 'bg-blue-500/10' : ''}`}
                                                            >
                                                                <div className="font-medium text-white">{n.title}</div>
                                                                <div className="text-gray-400 truncate mt-0.5">{n.body}</div>
                                                                <div className="text-xs text-gray-500 mt-1">
                                                                    {n.project_name && `${n.project_name} · `}
                                                                    {n.created_at && new Date(n.created_at).toLocaleString()}
                                                                </div>
                                                            </button>
                                                        </li>
                                                    ))}
                                                </ul>
                                            )}
                                        </div>
                                )}
                            </div>

                            {/* Share Project - only for project owner */}
                            {selectedProjectId !== null && isProjectOwner && (
                                <button
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setIsShareModalOpen(true);
                                    }}
                                    className="p-2 rounded-lg transition-colors text-gray-300 hover:bg-white/20 bg-white/10"
                                    title="Share project"
                                >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
                                    </svg>
                                </button>
                            )}
                        </div>
                        
                        {/* Settings and Sign Out */}
                        <div className="flex items-center space-x-2">
                            <button
                                onClick={() => setIsSettingsOpen(true)}
                                className="w-10 h-10 bg-gradient-to-r from-purple-500 to-blue-600 hover:from-purple-600 hover:to-blue-700 text-white rounded-lg shadow-lg hover:shadow-purple-500/25 transition-all duration-300 flex items-center justify-center group"
                                title="Settings"
                            >
                                <svg className="w-5 h-5 group-hover:rotate-90 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                </svg>
                            </button>
                                                        {isAuthenticated && (
                                                            <>
                                                                                                <button
                                                                                                    onClick={() => navigate('/profile', { state: { from: location.pathname } })}
                                                                    className="w-10 h-10 bg-gradient-to-r from-indigo-500 to-indigo-600 hover:from-indigo-600 hover:to-indigo-700 text-white rounded-lg shadow-lg hover:shadow-indigo-500/25 transition-all duration-300 flex items-center justify-center group"
                                                                    title="Profile"
                                                                >
                                                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5.121 17.804A13.937 13.937 0 0112 15c2.5 0 4.847.593 6.879 1.804M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                                                    </svg>
                                                                </button>

                                                                <button
                                                                        onClick={signOut}
                                                                        className="w-10 h-10 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white rounded-lg shadow-lg hover:shadow-red-500/25 transition-all duration-300 flex items-center justify-center group"
                                                                        title="Sign Out"
                                                                >
                                                                        <svg className="w-5 h-5 group-hover:scale-110 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                                                                        </svg>
                                                                </button>
                                                            </>
                                                        )}
                        </div>
                        

                    </div>
                </div>

                {/* Mobile Project Header */}
                <div className="lg:hidden bg-white/5 backdrop-blur-lg border-b border-white/10 px-4 py-3">
                    <div className="flex items-center justify-between">
                        <h2 className="text-lg font-medium text-white">
                            {selectedProjectId !== null ? (selectedProjectName || `Project ${selectedProjectId}`) : 'Select a project'}
                        </h2>
                        {selectedProjectId !== null && (
                            <span className="text-xs text-gray-300 bg-white/10 px-2 py-1 rounded-full">
                                {activeTab === 'requirements' && 'Requirements'}
                                {activeTab === 'git' && `Git (${selectedBranch})`}
                                {activeTab === 'sandbox' && 'Sandbox'}
                            </span>
                        )}
                    </div>
                </div>

                {/* Tab Navigation */}
                {selectedProjectId !== null && (
                    <div className="bg-white/5 backdrop-blur-lg border-b border-white/10">
                        <div className="flex items-center justify-between p-2">
                            <div className="flex space-x-1 flex-1">
                                <button
                                    onClick={() => {
                                        setActiveTab('requirements');
                                        setActiveSubmenu(null);
                                    }}
                                    className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${
                                        activeTab === 'requirements'
                                            ? 'bg-gradient-to-r from-purple-500 to-blue-600 text-white shadow-lg'
                                            : 'text-gray-300 hover:text-white hover:bg-white/10'
                                    }`}
                                >
                                    Requirements
                                </button>
                                
                                {/* Sandbox tab - shown when any feature build is in progress OR discovery has proceeded */}
                                {(hasInProgressBuild || discoveryProceeded) && (
                                    <button
                                        onClick={() => {
                                            setActiveTab('sandbox');
                                            if (!activeSubmenu || activeTab !== 'sandbox') {
                                                setActiveSubmenu('preview');
                                            }
                                        }}
                                        className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${
                                            activeTab === 'sandbox'
                                                ? 'bg-gradient-to-r from-teal-500 to-cyan-600 text-white shadow-lg'
                                                : 'text-gray-300 hover:text-white hover:bg-white/10'
                                        }`}
                                    >
                                        Sandbox {sandboxBranch && sandboxCommit ? `(${sandboxBranch}@${sandboxCommit.substring(0, 7)})` : ''}
                                    </button>
                                )}
                                
                                <button
                                    onClick={() => {
                                        setActiveTab('git');
                                        if (!activeSubmenu) {
                                            setActiveSubmenu('architecture');
                                        }
                                    }}
                                    className={`flex-1 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 ${
                                        activeTab === 'git'
                                            ? 'bg-gradient-to-r from-green-500 to-emerald-600 text-white shadow-lg'
                                            : 'text-gray-300 hover:text-white hover:bg-white/10'
                                    }`}
                                >
                                    Git ({selectedBranch})
                                </button>
                            </div>
                            {/* Terminal + chat: visible below lg where the desktop header is hidden (Requirements & Sandbox) */}
                            {(activeTab === 'requirements' || activeTab === 'sandbox') && (
                                <div className="flex items-center space-x-2 shrink-0 lg:hidden">
                                    <button
                                        type="button"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            toggleBottomPanel();
                                        }}
                                        className={`p-2 rounded-lg transition-colors text-gray-300 hover:bg-white/20 ${
                                            isBottomPanelOpen
                                                ? 'bg-green-500/20 border border-green-500/30'
                                                : 'bg-white/10'
                                        }`}
                                        title={`Toggle Terminal ${isBottomPanelOpen ? '(Open)' : '(Closed)'}`}
                                    >
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 9l3 3-3 3m5 0h3M5 20h14a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                        </svg>
                                    </button>
                                    <button
                                        type="button"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            toggleRightPanel();
                                        }}
                                        className={`p-2 rounded-lg transition-colors text-gray-300 hover:bg-white/20 ${
                                            isRightPanelOpen
                                                ? 'bg-blue-500/20 border border-blue-500/30'
                                                : 'bg-white/10'
                                        }`}
                                        title={`Toggle Chat Panel ${isRightPanelOpen ? '(Open)' : '(Closed)'}`}
                                    >
                                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.0M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
                                        </svg>
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* Requirements: Documents vs Canvas */}
                        {activeTab === 'requirements' && projectHasFlows !== null && (
                            <div className="flex items-center justify-between px-4 pb-3 border-t border-white/10 pt-3">
                                <div className="flex items-center space-x-3 flex-1">
                                    <button
                                        type="button"
                                        onClick={() => setRequirementsPaneOverride('documents')}
                                        className={`px-6 py-3 rounded-lg text-sm font-semibold transition-all duration-300 ${
                                            requirementsPane === 'documents'
                                                ? 'bg-purple-500/25 text-purple-100 border-2 border-purple-400/50 shadow-lg shadow-purple-500/10'
                                                : 'text-gray-400 hover:text-white hover:bg-white/10 border-2 border-transparent'
                                        }`}
                                    >
                                        Documents
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() => setRequirementsPaneOverride('canvas')}
                                        className={`px-6 py-3 rounded-lg text-sm font-semibold transition-all duration-300 ${
                                            requirementsPane === 'canvas'
                                                ? 'bg-cyan-500/20 text-cyan-200 border-2 border-cyan-500/40 shadow-lg shadow-cyan-500/10'
                                                : 'text-gray-400 hover:text-white hover:bg-white/10 border-2 border-transparent'
                                        }`}
                                    >
                                        Canvas
                                    </button>
                                </div>
                                <div className="flex items-center space-x-2 ml-4 shrink-0">
                                    <button
                                        type="button"
                                        onClick={handleRefresh}
                                        className="flex items-center space-x-2 px-4 py-2 text-sm font-semibold bg-gradient-to-r from-blue-500/20 to-cyan-500/20 border border-blue-400/30 rounded-lg text-blue-300 hover:from-blue-500/30 hover:to-cyan-500/30 hover:border-blue-400/50 transition-all duration-300 shadow-lg hover:shadow-blue-500/25"
                                        title="Refresh data"
                                    >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                        </svg>
                                        <span>Refresh</span>
                                    </button>
                                </div>
                            </div>
                        )}
                        
                        {/* Submenu Navigation - shown for Git and Sandbox tabs */}
                        {(activeTab === 'git' || activeTab === 'sandbox') && (
                            <div className="flex items-center justify-between px-4 pb-3 border-t border-white/10 pt-3">
                                <div className="flex items-center space-x-3 flex-1">
                                    {activeTab === 'sandbox' && (
                                        <button
                                            onClick={() => setActiveSubmenu('preview')}
                                            className={`px-6 py-3 rounded-lg text-sm font-semibold transition-all duration-300 ${
                                                activeSubmenu === 'preview'
                                                    ? 'bg-teal-500/20 text-teal-300 border-2 border-teal-500/40 shadow-lg shadow-teal-500/10'
                                                    : 'text-gray-400 hover:text-white hover:bg-white/10 border-2 border-transparent'
                                            }`}
                                        >
                                            Preview
                                        </button>
                                    )}
                                    <button
                                        onClick={() => setActiveSubmenu('architecture')}
                                        className={`px-6 py-3 rounded-lg text-sm font-semibold transition-all duration-300 ${
                                            activeSubmenu === 'architecture'
                                                ? 'bg-blue-500/20 text-blue-300 border-2 border-blue-500/40 shadow-lg shadow-blue-500/10'
                                                : 'text-gray-400 hover:text-white hover:bg-white/10 border-2 border-transparent'
                                        }`}
                                    >
                                        Architecture
                                    </button>
                                    <button
                                        onClick={() => setActiveSubmenu('code')}
                                        className={`px-6 py-3 rounded-lg text-sm font-semibold transition-all duration-300 ${
                                            activeSubmenu === 'code'
                                                ? 'bg-green-500/20 text-green-300 border-2 border-green-500/40 shadow-lg shadow-green-500/10'
                                                : 'text-gray-400 hover:text-white hover:bg-white/10 border-2 border-transparent'
                                        }`}
                                    >
                                        Code
                                    </button>
                                    <button
                                        onClick={() => setActiveSubmenu('documents')}
                                        className={`px-6 py-3 rounded-lg text-sm font-semibold transition-all duration-300 ${
                                            activeSubmenu === 'documents'
                                                ? 'bg-orange-500/20 text-orange-300 border-2 border-orange-500/40 shadow-lg shadow-orange-500/10'
                                                : 'text-gray-400 hover:text-white hover:bg-white/10 border-2 border-transparent'
                                        }`}
                                    >
                                        Documents
                                    </button>
                                </div>
                                
                                {/* Right side: Branch selector (Git only), Refresh, and Settings */}
                                <div className="flex items-center space-x-2 ml-4">
                                    {/* Branch dropdown for Git tab */}
                                    {activeTab === 'git' && (
                                        <div className="flex items-center space-x-2 bg-white/5 border border-white/10 rounded-lg px-3 py-1.5">
                                            <svg className="w-3.5 h-3.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
                                            </svg>
                                            <label className="text-xs text-gray-400 font-medium">Branch:</label>
                                            <select
                                                value={selectedBranch}
                                                onChange={(e) => handleBranchChange(e.target.value)}
                                                className="bg-transparent border-none text-white text-xs font-medium focus:outline-none cursor-pointer pr-6 appearance-none"
                                                style={{
                                                    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='white'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                                                    backgroundRepeat: 'no-repeat',
                                                    backgroundPosition: 'right 0 center',
                                                    backgroundSize: '12px'
                                                }}
                                            >
                                                {availableBranches.length > 0 ? (
                                                    availableBranches.map(branch => (
                                                        <option key={branch} value={branch} style={{ backgroundColor: '#1f2937', color: 'white' }}>{branch}</option>
                                                    ))
                                                ) : (
                                                    <option value={selectedBranch} style={{ backgroundColor: '#1f2937', color: 'white' }}>{selectedBranch}</option>
                                                )}
                                            </select>
                                        </div>
                                    )}
                                    
                                    {/* Refresh Button */}
                                    <button
                                        onClick={handleRefresh}
                                        className="flex items-center space-x-2 px-4 py-2 text-sm font-semibold bg-gradient-to-r from-blue-500/20 to-cyan-500/20 border border-blue-400/30 rounded-lg text-blue-300 hover:from-blue-500/30 hover:to-cyan-500/30 hover:border-blue-400/50 transition-all duration-300 shadow-lg hover:shadow-blue-500/25"
                                        title="Refresh data"
                                    >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                                        </svg>
                                        <span>Refresh</span>
                                    </button>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Tab Content */}
                <div className="flex-1 overflow-hidden bg-white/5 backdrop-blur-lg">
                    {selectedProjectId !== null ? (
                        <div className="h-full w-full">
                            {activeTab === 'requirements' && (
                                projectHasFlows === null ? (
                                    <div className="flex h-full flex-col items-center justify-center bg-[#0A071B] text-white">
                                        <div
                                            className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-purple-500 border-t-transparent"
                                            aria-hidden
                                        />
                                        <p className="mt-4 text-base font-medium text-white/95">Loading project</p>
                                        <p className="mt-2 text-sm text-purple-200/90">Fetching project…</p>
                                    </div>
                                ) : projectHasFlows === false ? (
                                    <DiscoveryProceedPauseProvider>
                                        <div
                                            className={`h-full min-h-0 flex flex-col transition-all duration-300 ${
                                                isRightPanelOpen ? 'lg:pr-[clamp(480px,37.5vw,600px)]' : ''
                                            }`}
                                        >
                                            <div
                                                className={
                                                    requirementsPane === 'documents'
                                                        ? 'flex min-h-0 flex-1 flex-col overflow-hidden'
                                                        : 'hidden'
                                                }
                                            >
                                                <ProductDiscoveryChat
                                                    projectName={selectedProjectName || `Project ${selectedProjectId}`}
                                                    initialPrompt={selectedProjectInitialPrompt}
                                                    projectId={selectedProjectId}
                                                    onBack={() => {}}
                                                    onBuild={(existingProjectId) =>
                                                        handleDiscoveryBuildFromProject(
                                                            typeof existingProjectId === 'number' ? existingProjectId : undefined
                                                        )
                                                    }
                                                    isBuilding={discoveryBuildBusy}
                                                    onProceed={() => setDiscoveryProceeded(true)}
                                                    onUserFlowDraft={handleDiscoveryUserFlowDraft}
                                                    onRequestGitSettings={(scopedProjectId) => {
                                                        setGitSettingsScopeProjectId(scopedProjectId);
                                                        setIsGitSettingsOpen(true);
                                                    }}
                                                />
                                            </div>
                                            <div
                                                className={
                                                    requirementsPane === 'canvas'
                                                        ? 'flex min-h-0 flex-1 flex-col overflow-hidden'
                                                        : 'hidden'
                                                }
                                            >
                                                <ErrorBoundary>
                                                    <CanvasViewer
                                                        projectId={
                                                            selectedProjectId != null ? String(selectedProjectId) : null
                                                        }
                                                        refreshTrigger={canvasRefreshNonce}
                                                        onToggleSidebar={toggleSidebar}
                                                        showCanvas={true}
                                                        isBottomPanelOpen={isBottomPanelOpen}
                                                        deepLinkOpen={deepLinkOpen}
                                                        onDeepLinkHandled={() => setDeepLinkOpen(null)}
                                                        isRightPanelOpen={isRightPanelOpen}
                                                        onToggleBottomPanel={toggleBottomPanel}
                                                        onToggleRightPanel={toggleRightPanel}
                                                        canEdit={canEditProject}
                                                        initialRequirements={requirementsSeed}
                                                        onInitialRequirementsConsumed={() =>
                                                            setRequirementsSeed(null)
                                                        }
                                                        outlineBridgeActive={requirementsPane === 'canvas'}
                                                    />
                                                </ErrorBoundary>
                                            </div>
                                        </div>
                                        {requirementsPane === 'documents' && (
                                            <VishCoderChatPanel
                                                isOpen={isRightPanelOpen}
                                                onToggle={toggleRightPanel}
                                                page="project_chat"
                                                projectId={selectedProjectId}
                                                channel="discovery"
                                            />
                                        )}
                                    </DiscoveryProceedPauseProvider>
                                ) : (
                                    <>
                                        {/*
                                          Keep CanvasViewer mounted when switching Documents ↔ Canvas so we
                                          do not refetch `/requirements` and rebuild the graph on every toggle.
                                        */}
                                        <div
                                            className={
                                                requirementsPane === 'documents'
                                                    ? `flex h-full min-h-0 flex-col transition-all duration-300 ${
                                                          isRightPanelOpen ? 'lg:pr-[clamp(480px,37.5vw,600px)]' : ''
                                                      }`
                                                    : 'hidden h-full min-h-0 flex-col'
                                            }
                                        >
                                            <DiscoveryDocumentsFromChat
                                                projectName={
                                                    selectedProjectName || `Project ${selectedProjectId}`
                                                }
                                                projectId={selectedProjectId}
                                            />
                                        </div>
                                        <div
                                            className={
                                                requirementsPane === 'canvas'
                                                    ? 'flex h-full min-h-0 w-full flex-1 flex-col overflow-hidden'
                                                    : 'hidden h-full min-h-0 w-full flex-1 flex-col overflow-hidden'
                                            }
                                        >
                                            <ErrorBoundary>
                                                <CanvasViewer
                                                    projectId={
                                                        selectedProjectId != null
                                                            ? String(selectedProjectId)
                                                            : null
                                                    }
                                                    refreshTrigger={canvasRefreshNonce}
                                                    onToggleSidebar={toggleSidebar}
                                                    showCanvas={true}
                                                    isBottomPanelOpen={isBottomPanelOpen}
                                                    deepLinkOpen={deepLinkOpen}
                                                    onDeepLinkHandled={() => setDeepLinkOpen(null)}
                                                    isRightPanelOpen={isRightPanelOpen}
                                                    onToggleBottomPanel={toggleBottomPanel}
                                                    onToggleRightPanel={toggleRightPanel}
                                                    canEdit={canEditProject}
                                                    initialRequirements={requirementsSeed}
                                                    onInitialRequirementsConsumed={() =>
                                                        setRequirementsSeed(null)
                                                    }
                                                    outlineBridgeActive={requirementsPane === 'canvas'}
                                                />
                                            </ErrorBoundary>
                                        </div>
                                        {requirementsPane === 'documents' && (
                                            <VishCoderChatPanel
                                                isOpen={isRightPanelOpen}
                                                onToggle={toggleRightPanel}
                                                page="project_chat"
                                                projectId={selectedProjectId}
                                                channel="discovery"
                                            />
                                        )}
                                    </>
                                )
                            )}
                            
                            {/* Git Tab with Submenu */}
                            {activeTab === 'git' && (
                                <>
                                    {activeSubmenu === 'architecture' && (
                                        <ArchitectureTab 
                                            projectId={selectedProjectId} 
                                            refreshTrigger={architectureRefreshTrigger}
                                            isRightPanelOpen={isRightPanelOpen}
                                            onToggleRightPanel={toggleRightPanel}
                                            source="git"
                                            canEdit={canEditProject}
                                        />
                                    )}
                                    {activeSubmenu === 'code' && (
                                        <CodeTab 
                                            projectId={selectedProjectId} 
                                            refreshTrigger={codeRefreshTrigger}
                                            isRightPanelOpen={isRightPanelOpen}
                                            onToggleRightPanel={toggleRightPanel}
                                            source="git"
                                            canEdit={canEditProject}
                                        />
                                    )}
                                    {activeSubmenu === 'documents' && (
                                        <DocumentViewer 
                                            projectId={selectedProjectId} 
                                            refreshTrigger={documentsRefreshTrigger}
                                            isRightPanelOpen={isRightPanelOpen}
                                            onToggleRightPanel={toggleRightPanel}
                                            source="git"
                                            canEdit={canEditProject}
                                        />
                                    )}
                                    {!activeSubmenu && (
                                        <div className="h-full w-full flex items-center justify-center p-8">
                                            <div className="text-center">
                                                <p className="text-gray-400">Select a submenu option above</p>
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}
                            
                            {/* Sandbox Tab with Submenu */}
                            {activeTab === 'sandbox' && (
                                <>
                                    {activeSubmenu === 'preview' && (
                                        <div className="p-4 h-full relative">
                                            <Preview 
                                                userId={(user as any)?.id || TokenManager.getUserId() || null}
                                                projectId={selectedProjectId}
                                                title="Sandbox Preview" 
                                                onElementSelected={(info) => setPreviewSelection(info)} 
                                            />
                                            {previewSelection && (
                                                <div className="absolute left-4 bottom-4 bg-neutral-900/80 text-white p-2 rounded text-xs">
                                                    <div><strong>Last selection:</strong> {previewSelection.cssPath || (previewSelection.tagName || 'coords')}</div>
                                                </div>
                                            )}
                                            {/* VishCoder Chat Panel */}
                                            <VishCoderChatPanel 
                                                isOpen={isRightPanelOpen} 
                                                onToggle={toggleRightPanel}
                                                page="preview"
                                                projectId={selectedProjectId}
                                            />
                                        </div>
                                    )}
                                    {activeSubmenu === 'architecture' && (
                                        <ArchitectureTab 
                                            projectId={selectedProjectId} 
                                            refreshTrigger={architectureRefreshTrigger}
                                            isRightPanelOpen={isRightPanelOpen}
                                            onToggleRightPanel={toggleRightPanel}
                                            source="sandbox"
                                            userId={(user as any)?.id || TokenManager.getUserId() || null}
                                            canEdit={canEditProject}
                                        />
                                    )}
                                    {activeSubmenu === 'code' && (
                                        <CodeTab 
                                            projectId={selectedProjectId} 
                                            refreshTrigger={codeRefreshTrigger}
                                            isRightPanelOpen={isRightPanelOpen}
                                            onToggleRightPanel={toggleRightPanel}
                                            source="sandbox"
                                            userId={(user as any)?.id || TokenManager.getUserId() || null}
                                            canEdit={canEditProject}
                                        />
                                    )}
                                    {activeSubmenu === 'documents' && (
                                        <DocumentViewer 
                                            projectId={selectedProjectId} 
                                            refreshTrigger={documentsRefreshTrigger}
                                            isRightPanelOpen={isRightPanelOpen}
                                            onToggleRightPanel={toggleRightPanel}
                                            source="sandbox"
                                            userId={(user as any)?.id || TokenManager.getUserId() || null}
                                            canEdit={canEditProject}
                                        />
                                    )}
                                    {!activeSubmenu && (
                                        <div className="h-full w-full flex items-center justify-center p-8">
                                            <div className="text-center">
                                                <p className="text-gray-400">Select a submenu option above</p>
                                            </div>
                                        </div>
                                    )}
                                </>
                            )}
                            {activeTab === 'sandbox' && selectedProjectId !== null && (
                                <BuildChannelHandler
                                    isBottomPanelOpen={isBottomPanelOpen}
                                    onToggleBottomPanel={toggleBottomPanel}
                                    buildSummaryData={null}
                                    setBuildSummaryData={() => {}}
                                    setIsBuildSummaryModalOpen={() => {}}
                                    projectId={selectedProjectId}
                                />
                            )}
                            {activeTab === 'requirements' &&
                                selectedProjectId !== null &&
                                requirementsPane === 'documents' && (
                                    <BuildChannelHandler
                                        isBottomPanelOpen={isBottomPanelOpen}
                                        onToggleBottomPanel={toggleBottomPanel}
                                        buildSummaryData={null}
                                        setBuildSummaryData={() => {}}
                                        setIsBuildSummaryModalOpen={() => {}}
                                        projectId={selectedProjectId}
                                    />
                                )}
                        </div>
                    ) : (
                        /* Empty State */
                        <DiscoveryProceedPauseProvider>
                            <div
                                className={`
                                    relative h-full flex items-center justify-center px-6 transition-all duration-300 overflow-hidden
                                    ${isCreateProjectChatMode ? 'lg:pr-[clamp(480px,37.5vw,600px)]' : ''}
                                `}
                            >
                                {/* Apple-minimal + subtle depth: soft orbs, radial light, slow ambient pulse */}
                                <div className="pointer-events-none absolute inset-0" aria-hidden>
                                    <div className="absolute inset-0 bg-[radial-gradient(ellipse_85%_55%_at_50%_-8%,rgba(139,92,246,0.22),transparent_58%)] animate-ambient-shift" />
                                    <div className="absolute -top-40 left-1/2 h-[min(520px,80vh)] w-[min(520px,90vw)] -translate-x-1/2 rounded-full bg-violet-500/[0.14] blur-3xl" />
                                    <div className="absolute -bottom-24 right-[8%] h-72 w-72 rounded-full bg-purple-600/[0.12] blur-3xl" />
                                    <div className="absolute top-1/3 left-[6%] h-48 w-48 rounded-full bg-indigo-500/[0.08] blur-2xl" />
                                    <svg
                                        className="absolute inset-0 h-full w-full opacity-[0.07]"
                                        xmlns="http://www.w3.org/2000/svg"
                                        preserveAspectRatio="xMidYMid slice"
                                    >
                                        <defs>
                                            <linearGradient id="net" x1="0%" y1="0%" x2="100%" y2="100%">
                                                <stop offset="0%" stopColor="rgb(167,139,250)" stopOpacity="0.5" />
                                                <stop offset="100%" stopColor="rgb(139,92,246)" stopOpacity="0.2" />
                                            </linearGradient>
                                        </defs>
                                        <path
                                            d="M0,45% Q25%,35% 50%,48% T100%,42%"
                                            fill="none"
                                            stroke="url(#net)"
                                            strokeWidth="0.6"
                                        />
                                        <path
                                            d="M0,62% Q40%,72% 100%,58%"
                                            fill="none"
                                            stroke="url(#net)"
                                            strokeWidth="0.45"
                                            opacity="0.7"
                                        />
                                        <path
                                            d="M15%,100% Q50%,88% 85%,100%"
                                            fill="none"
                                            stroke="url(#net)"
                                            strokeWidth="0.4"
                                            opacity="0.5"
                                        />
                                    </svg>
                                </div>
                                <div className="relative z-[1] w-full max-w-[67rem]">
                                    <header className="mb-8 text-center md:mb-10">
                                        <h2 className="text-4xl font-semibold tracking-tight text-white md:text-5xl">
                                            Your idea. Our agents. A real product.
                                        </h2>
                                    </header>
                                    <CreateProjectForm
                                        onProjectCreated={handleProjectCreated}
                                        shouldExpand={true}
                                        minimalComposer={true}
                                        onChatModeChange={setIsCreateProjectChatMode}
                                    />
                                </div>
                                <VishCoderChatPanel
                                    isOpen={isCreateProjectChatMode}
                                    onToggle={() => setIsCreateProjectChatMode(prev => !prev)}
                                    page="project_chat"
                                    channel="discovery"
                                />
                            </div>
                        </DiscoveryProceedPauseProvider>
                    )}
                </div>
            </div>
            </BottomPanelInsetProvider>

            {/* Mobile Bottom Action Bar (Optional) */}
            {isMobile && selectedProjectId !== null && (
                <div className="fixed bottom-0 left-0 right-0 bg-white/5 backdrop-blur-lg border-t border-white/10 p-4 safe-area-inset z-30">
                    <div className="flex justify-center">
                        <button
                            onClick={toggleSidebar}
                            className="flex items-center px-6 py-3 bg-white/10 hover:bg-white/20 text-white rounded-xl transition-all duration-300 border border-white/20"
                        >
                            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h8m-8 6h16" />
                            </svg>
                            Projects
                        </button>
                    </div>
                </div>
            )}

            {/* Settings Modal */}
            <Settings 
                isOpen={isSettingsOpen} 
                onClose={() => setIsSettingsOpen(false)}
                initialTab={settingsInitialTab}
            />
            
            {/* Git Settings Modal — scope from sidebar, discovery (explicit id), or browser defaults (null) */}
            <GitSettings
                isOpen={isGitSettingsOpen}
                onClose={() => {
                    setIsGitSettingsOpen(false);
                    setGitSettingsScopeProjectId(undefined);
                }}
                projectId={gitSettingsEffectiveProjectId}
                onSettingsUpdated={() => {
                    if (activeTab === 'git') {
                        const loadBranches = async () => {
                            try {
                                const settings = getProjectGitSettingsFromStorage(gitSettingsEffectiveProjectId);
                                if (settings?.repo_url) {
                                    const repoInfo = GitHubService.parseRepoPath(settings.repo_url);
                                    if (repoInfo) {
                                        try {
                                            const branches = await GitHubService.getBranches(
                                                repoInfo.owner,
                                                repoInfo.repo,
                                                settings.token
                                            );
                                            setAvailableBranches(branches.map(b => b.name));
                                            if (settings.branch) {
                                                setSelectedBranch(settings.branch);
                                            }
                                        } catch (error) {
                                            console.error('Failed to load branches:', error);
                                        }
                                    }
                                }
                            } catch (error) {
                                console.error('Failed to load GitHub settings:', error);
                            }
                        };
                        loadBranches();
                    }
                    if (activeSubmenu) {
                        if (activeSubmenu === 'architecture') {
                            setArchitectureRefreshTrigger(prev => prev + 1);
                        } else if (activeSubmenu === 'code') {
                            setCodeRefreshTrigger(prev => prev + 1);
                        } else if (activeSubmenu === 'documents') {
                            setDocumentsRefreshTrigger(prev => prev + 1);
                        }
                    }
                }}
            />

            {/* Share Project Modal */}
            <ShareProjectModal
                isOpen={isShareModalOpen}
                onClose={() => setIsShareModalOpen(false)}
                projectId={selectedProjectId ?? ''}
                projectName={selectedProjectName || undefined}
            />
        </div>
        </RequirementsCanvasOutlineProvider>
    );
};

export default ProjectDashboard;